import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { keyframes } from 'styled-components';
import { MediaQuery, Palette } from 'core/config';

const HeaderContainer = styled.div`
  @media (max-width: 1024px) {
    padding: 130px 2rem 0px 2rem;
  }

  @media ${MediaQuery.mobileL} {
    padding: 230px 30px 8px 30px;
  }

  @media ${MediaQuery.tablet} {
    flex-direction: row;
    padding: 155px 105px 8px 105px;
  }

  @media ${MediaQuery.laptopS} {
    padding: 185px 125px 8px 125px;
  }

  @media ${MediaQuery.laptopM} {
    padding: 185px 125px 8px 125px;
  }

  @media ${MediaQuery.laptopL} {
    padding: 185px 125px 8px 125px;
  }

  @media ${MediaQuery.desktopS} {
    padding: 185px 135px 8px 135px;
  }

  @media ${MediaQuery.desktopM} {
    padding: 185px 180px 8px 180px;
  }

  @media ${MediaQuery.desktopL} {
    padding: 185px 180px 8px 180px;
  }
`;

const Container = styled.div`
  @media (max-width: 1024px) {
    padding: 2rem 0rem 80px 2rem;
  }
  @media ${MediaQuery.mobileL} {
    padding: 2rem 16px 80px 16px;
  }
  @media ${MediaQuery.tablet} {
    flex-direction: row;
    padding: 2rem 72px 80px 72px;
  }
  @media ${MediaQuery.laptopS} {
    padding: 2rem 80px 80px 80px;
  }
  @media ${MediaQuery.laptopM} {
    padding: 2rem 80px 80px 80px;
  }
  @media ${MediaQuery.laptopL} {
    padding: 2rem 80px 80px 80px;
  }
  @media ${MediaQuery.desktopS} {
    padding: 2rem 80px 80px 80px;
  }
  @media ${MediaQuery.desktopM} {
    padding: 2rem 124px 80px 124px;
  }
  @media ${MediaQuery.desktopL} {
    padding: 2rem 124px 80px 124px;
  }
`;

const HeaderSection = styled.div`
  padding: 40px 0px 20px 0px;
  gap: 30px;

  @media ${MediaQuery.tablet} {
    gap: 48px;
  }
  @media ${MediaQuery.laptopM} {
    gap: 48px;
  }
`;

const Title = styled.h1`
  overflow: hidden;
  border-right: 4px solid white;
  display: inline-block;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  color: ${Palette.BLACK};
  font-size: calc(1.325em + 4vw);
  line-height: 1.15em;
  white-space: nowrap;
  overflow: hidden;
  border-right: 4px solid white;
  @media ${MediaQuery.tablet} {
    width: 40vw;
    font-size: 3.5em;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 4.5em;
  }
  @media ${MediaQuery.desktopS} {
    width: 100%;
    font-size: 5em;
  }
  animation: ${keyframes`
    0% { width: 0; }
    100% { width: 100%; }
  `} 4s steps(40) 1s forwards, blink 0.75s step-end infinite;

  @keyframes blink {
    50% {
      border-color: transparent;
    }
  }
`;

const Subheader = styled.div`
  font-family: 'Poppins-Light';
  font-weight: 300;
  font-size: 22px;
  line-height: 180%;
  letter-spacing: 0;
  color: #000000;
  max-width: 663.76px;
  margin-bottom: 25px;
  margin-top: 0;
  text-align: left;
  @media (max-width: 768px) {
    font-size: 18px;
    max-width: 100%;
  }
  @media (max-width: 600px) {
    font-family: 'Poppins-Light', 'Poppins', sans-serif;
    font-weight: 300;
    font-size: 16px;
    line-height: 150%;
    letter-spacing: 0;
    color: #000000;
    max-width: 358px;
    width: 100%;
    margin-bottom: 25px;
  }

  width: 100%;
  color: ${Palette.BLACK};
  font-family: "Poppins-Light";
  font-size: 16px;
  line-height: 180%;
  white-space: pre-line;
  padding-top: 24px;
`;

const JobBoardContainer = styled.div`
  width: 100%;
  margin-top: 1rem;
  padding: 1rem 0;
  
  /* Let the Rippling job board determine its own height */
  #rr-job-board {
    width: 100%;
    min-height: 400px; /* Minimum height for loading state */
    height: auto; /* Allow natural expansion */
    overflow: visible; /* Ensure content is not clipped */
  }
`;

const GlobalStyle = createGlobalStyle`
  .desktop-only { display: inline; }
  .mobile-only { display: none; }
  @media (max-width: 768px) {
    .desktop-only { display: none; }
    .mobile-only { display: inline; }
  }
  
  /* Ensure Rippling job board can expand naturally */
  #rr-job-board {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }
  
  /* Override any Rippling container constraints */
  #rr-job-board * {
    max-height: none !important;
    overflow: visible !important;
  }
`;

export default function RipplingCareersPage(): JSX.Element 
{
    useEffect(() => 
    {
    // Reset scroll position when component mounts
        window.scrollTo(0, 0);
    
        // Load Rippling script dynamically
        const script = document.createElement('script');
        script.src = 'https://static-assets.ripplingcdn.com/ats/embeds/job-board.v1.js';
        script.async = true;
        document.head.appendChild(script);

        // Cleanup function to remove script when component unmounts
        return () => 
        {
            const existingScript = document.querySelector('script[src="https://static-assets.ripplingcdn.com/ats/embeds/job-board.v1.js"]');
            if (existingScript) 
            {
                document.head.removeChild(existingScript);
            }
        };
    }, []);

    return (
        <>
            <HeaderContainer>
                <HeaderSection>
                    <Title>Work at Speer</Title>
                    <Subheader>
                        <span className="desktop-only">Explore our open roles and dive into the projects that define<br />our culture of innovation and collaboration.</span>
                        <span className="mobile-only">Explore our open roles and dive into the projects that define our culture of innovation and collaboration.</span>
                    </Subheader>
                </HeaderSection>
            </HeaderContainer>
            <GlobalStyle />
            <Container>
                <JobBoardContainer>
                    {/* Rippling Recruiting: Place this where you want your job board to appear. */}
                    <div id="rr-job-board" data-job-board-id="speer-technologies-inc"></div>
                </JobBoardContainer>
            </Container>
        </>
    );
}